import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import useStyles from 'isomorphic-style-loader/useStyles';
import isEqual from 'lodash/isEqual';
import { RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { StringKeyOf } from 'type-fest';

import type { Filter } from '@models';
import Pills from '@components/pills';
import type { PillElement } from '@components/pills/pills';
import ToolbarButton from '@components/toolbar-button';

import {
  activeFilterKeys,
  filterDefinitions,
  shouldRenderFilterBar,
} from './filter-utils';
import s from './filter-bar.scss';

export interface FilterBarProps<State> {
  className?: string;
  defaultValues?: Partial<State>;
  filters: Filter<StringKeyOf<State>>[];
  onChange: (values: Partial<State>) => void;
  values: Partial<State>;
}

/**
 * Renders the active filter pills for a list view, with an inline Reset
 * control that appears when the current values differ from `defaultValues`.
 *
 * Pair with `FilterPicker` (which provides the "Filter" dropdown trigger) for
 * a complete filter experience. The bar animates in and out as filters are
 * applied or cleared, and renders nothing when there's no active state.
 *
 * Pills follow the key order of `values`, so a filter added to an existing set
 * lands at the end of the row rather than in the middle of it.
 */
export const FilterBar = <State,>({
  className,
  defaultValues = {},
  filters: availableFilters,
  onChange,
  values,
}: FilterBarProps<State>) => {
  useStyles(s);

  const filtersChanged = useMemo<boolean>(
    () => !isEqual(values, defaultValues),
    [values, defaultValues],
  );

  const selectedFilters = useMemo<PillElement[]>(() => {
    const definitions = filterDefinitions(availableFilters);

    return activeFilterKeys(values)
      .map(key => definitions.get(key))
      .filter((field): field is Filter<StringKeyOf<State>> => !!field)
      .map<PillElement>(field => ({
        ...field,
        // @ts-expect-error -- This is fiddly to type correctly due to inferred State keys
        data: values[field.key as unknown as keyof State],
        clickable: false,
        removable: true,

        // Being able to clear an Enum doesn't really make sense:
        // if you want to get rid of the selected value, you can simply remove the filter.
        clearable: field.typeName !== 'Enum',
        editorInputClass: field.className,
      }));
  }, [availableFilters, values]);

  const visible = shouldRenderFilterBar(
    values,
    availableFilters,
    defaultValues,
  );

  // Seed from `visible`: a row present on first render skips the entrance
  // animation (`initial={false}`), so no callback fires to flip the gate.
  const [idle, setIdle] = useState(visible);

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          className={classNames(s.motion, idle && s.idle)}
          key="filter-bar"
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          initial={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onAnimationStart={() => setIdle(false)}
          onAnimationComplete={() => setIdle(true)}
        >
          <div className={classNames(s.root, className)}>
            <Pills
              pillClassName={s.filterPill}
              displayAfter={
                filtersChanged ? (
                  <ToolbarButton
                    content="Reset Filters"
                    icon={<RotateCcw />}
                    onClick={() => onChange(defaultValues)}
                  />
                ) : undefined
              }
              elements={selectedFilters}
              editInline
              onRemovePill={pill =>
                onChange(
                  (Object.keys(values) as (keyof State)[])
                    .filter(key => key !== pill.key)
                    .reduce<Partial<State>>(
                      (obj, key) => ({ ...obj, [key]: values[key] }),
                      {},
                    ),
                )
              }
              onUpdatePill={pill => {
                onChange({ ...values, [pill.key]: pill.data });
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FilterBar;
